import { lazy, type ComponentType, type LazyExoticComponent } from "react";

/**
 * `React.lazy` for route pages, which load their code on first visit (#995).
 *
 * A page's chunk can 404 when a tab outlives a deploy. Vite's import helper
 * then fires `vite:preloadError`; the self-heal listener (`selfHeal.ts`, #917)
 * reloads the page once and calls `preventDefault()`, and the helper resolves
 * the import to `undefined` instead of rejecting. Plain `lazy()` would render
 * `undefined.default` and flash the route's error fallback, logging a bogus
 * render error, while the reload is under way. Here the import stays pending
 * instead, so the Suspense fallback remains until the page is replaced.
 *
 * When the one reload was already spent, the helper rethrows the original
 * error, so the route's `RouteErrorBoundary` shows its fallback as before.
 */
export const lazyPage = <T extends ComponentType>(
  load: () => Promise<{ default: T } | undefined>
): LazyExoticComponent<T> =>
  lazy(() => load().then(module => module ?? new Promise<{ default: T }>(() => {})));
